import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { workPlannerApi } from "@/services/workPlannerApi";
import { SERVICE_KEYS, serviceMeta, STATUS_KEYS, statusMeta, PRIORITIES, istToday } from "./mpShared";

const EMPTY = {
  title: "", client_id: "", service: "", description: "", assigned_users: [],
  priority: "medium", start_date: "", due_date: "", status: "planned", estimated_hours: "", notes: "",
};

/** Create or edit one deliverable. Writes through the planner's raw-write task endpoints. */
export default function TaskDialog({ open, onClose, task, defaults, clients, members, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editing = !!(task && (task.task_id || task._id));

  useEffect(() => {
    if (!open) return;
    setForm(task
      ? {
        title: task.title || "", client_id: task.client_id || "", service: task.service || "",
        description: task.description || "", assigned_users: task.assigned_users || [],
        priority: task.priority || "medium", start_date: (task.start_date || "").slice(0, 10),
        due_date: (task.due_date || "").slice(0, 10), status: task.status || "planned",
        estimated_hours: task.estimated_hours ?? "", notes: task.notes || "",
      }
      : { ...EMPTY, due_date: istToday(), ...(defaults || {}) });
  }, [open, task, defaults]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Task title is required");
    if (!form.client_id) return toast.error("Choose a client");
    setSaving(true);
    try {
      const payload = {
        ...form, title: form.title.trim(),
        estimated_hours: form.estimated_hours === "" ? null : Number(form.estimated_hours),
        start_date: form.start_date || form.due_date || null, due_date: form.due_date || null,
      };
      if (editing) await workPlannerApi.updateTask(task.task_id || task._id, payload);
      else await workPlannerApi.createTask(payload);
      toast.success(editing ? "Task updated" : "Task created");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${task.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await workPlannerApi.deleteTask(task.task_id || task._id); toast.success("Task deleted"); onSaved?.(); onClose(); }
    catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const toggleMember = (id) => set("assigned_users", form.assigned_users.includes(id) ? form.assigned_users.filter((x) => x !== id) : [...form.assigned_users, id]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Task title *</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} autoFocus data-testid="mp-task-title"
              placeholder="e.g. Creative #3 — Diwali offer" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-slate-600">Client *</label>
              <select value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Service type</label>
              <select value={form.service} onChange={(e) => set("service", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                <option value="">No service (not counted in targets)</option>
                {SERVICE_KEYS.map((k) => <option key={k} value={k}>{serviceMeta(k).label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="text-xs font-medium text-slate-600">Start date</label>
              <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Priority</label>
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Est. hours</label>
              <input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Status</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {STATUS_KEYS.map((k) => (
                <button key={k} type="button" onClick={() => set("status", k)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${form.status === k ? statusMeta(k).chip : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"}`}>
                  {statusMeta(k).label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Assigned to</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {members.length === 0 && <span className="text-xs text-slate-400">No team members found.</span>}
              {members.map((m) => (
                <button key={m.user_id} type="button" onClick={() => toggleMember(m.user_id)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${form.assigned_users.includes(m.user_id) ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                  {m.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <div>
            {editing && (
              <button onClick={remove} disabled={deleting} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50">
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="mp-task-save" className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create task"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
